<template>
  <div class="offers-section px-6 py-8 md:pt-10 md:pl-16 md:pr-12 xl:pl-32 text-left">
    <div class="flex mb-4 md:mb-8">
      <!-- Offer Icon -->
      <div
        v-if="content.icon.isIcon"
        class="offers-section__icon bg-primary-light w-14 h-14 rounded-full flex flex-shrink-0"
        v-dompurify-html:svg="iconSource"
      />

      <img
        v-else
        :alt="content.icon.alt"
        :src="content.icon.source"
        class="offers-section__image"
      />

      <div class="ml-4 md:ml-6">
        <!-- Title -->
        <h6 class="mb-1 md:mb-2 text-2xl text-black-darkest tracking-0.15px">
          {{ content.title }}
        </h6>

        <!-- Description -->
        <div v-dompurify-html="content.description" class="richtext"></div>
      </div>
    </div>

    <!-- Offers -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <OfferCard
        v-for="offer of specialOffers"
        :key="offer.uid"
        :content="offer"
      />
    </div>

    <!-- See more link -->
    <div class="flex justify-end mt-6 md:mt-4">
      <a
        :href="content.seeMoreUrl"
        target="_parent"
        class="k-link"
        data-testid="see-more-link"
        @click.prevent="onSeeMoreClick"
      >
        {{content.seeMoreText}}
      </a>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import OfferCard from '@/components/atoms/OfferCard/index.vue'
import { OffersSection } from '@/model'
import useRemoteIcon from '@/hooks/useRemoteIcon'
import { trackEvent } from '@/utils/analytics'
import { addAdobeDataLayerEventSeeMore } from '@/utils/adobe-analytics-data-layer'

export default defineComponent({
  components: {
    OfferCard
  },
  props: {
    content: {
      type: Object as () => OffersSection,
      required: true,
      validator: (value: OffersSection) => value instanceof OffersSection
    }
  },
  setup (props) {
    const specialOffers = props.content.offers.slice(0, 2)
    const iconSource = props.content.icon.isIcon && props.content.icon.source
      ? useRemoteIcon(props.content.icon.source)
      : null

    function onSeeMoreClick () {
      addAdobeDataLayerEventSeeMore()
      window.open(props.content.seeMoreUrl, '_parent')
    }

    return {
      specialOffers,
      iconSource,
      onSeeMoreClick
    }
  }
})
</script>

<style>
.offers-section {
  &__icon svg {
    @apply w-8 h-8 m-auto fill-current text-primary;
  }

  &__image {
    @apply w-14 h-14;
  }
}
</style>
